/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import {signal} from '@preact/signals-react';
import isObject from 'lodash.isobject';
import isString from 'lodash.isstring';
import {createMutator} from '@ion/applications/src/store/SignalUtils';
import UserPreferences from '@ion/core/src/preferences/UserPreferences';
import {
  TrackingFavoriteFilterState,
  TrackingFavoritePreferences,
  TrackingFilterDrawerCategoryId
} from './types';

export const TRACKING_FILTER_CATEGORY_ORDER_PREFERENCE = 'tracking.filterDrawer.categoryOrder';
export const TRACKING_FILTER_FAVORITES_PREFERENCE      = 'tracking:saved-filters';

/**
 * Stores whether the current-theme tracking search overlay is open.
 */
export const trackingSearchOverlayOpen = signal(false);

/**
 * Replaces the current-theme tracking search overlay open state.
 *
 * @param open Defines whether the search overlay is open.
 */
export const setTrackingSearchOverlayOpen = createMutator(trackingSearchOverlayOpen);

/**
 * Gets the current-theme tracking search overlay open state.
 *
 * @returns `true` when the search overlay is open, otherwise `false`.
 */
export const getTrackingSearchOverlayOpen = () => trackingSearchOverlayOpen.value;

/**
 * Subscribes to current-theme tracking search overlay open-state changes.
 *
 * @param onStoreChange Defines the callback invoked when the open state changes.
 * @returns A function that unsubscribes from search overlay changes.
 */
export const subscribeTrackingSearchOverlayOpen = (onStoreChange: () => void) => (
  trackingSearchOverlayOpen.subscribe(() => {
    onStoreChange();
  })
);

/**
 * Stores whether something outside the navigation has asked for the
 * current-theme tracking filter drawer to be opened. The drawer's own open
 * state belongs to the side navigation, which answers this request through
 * the same path its filter button takes, then clears it.
 */
export const trackingFilterOpenRequested = signal(false);

/**
 * Replaces the tracking filter drawer open request.
 *
 * @param requested Defines whether the drawer has been asked to open.
 */
export const setTrackingFilterOpenRequested = createMutator(trackingFilterOpenRequested);

/**
 * Gets the tracking filter drawer open request.
 *
 * @returns `true` when the drawer has been asked to open, otherwise `false`.
 */
export const getTrackingFilterOpenRequested = () => trackingFilterOpenRequested.value;

/**
 * Subscribes to tracking filter drawer open request changes.
 *
 * @param onStoreChange Defines the callback invoked when the request changes.
 * @returns A function that unsubscribes from open request changes.
 */
export const subscribeTrackingFilterOpenRequested = (onStoreChange: () => void) => (
  trackingFilterOpenRequested.subscribe(() => {
    onStoreChange();
  })
);

/**
 * Stores whether the current-theme tracking filter drawer is open - the
 * applied filter bar starts where the open drawer ends, never under it.
 */
export const trackingFilterDrawerOpen = signal(false);

/**
 * Replaces whether the tracking filter drawer is open.
 *
 * @param open Defines whether the drawer is open.
 */
export const setTrackingFilterDrawerOpen = createMutator(trackingFilterDrawerOpen);

/**
 * Reads persisted tracking filter favorites from user preferences.
 *
 * @returns Saved tracking favorite filter states keyed by favorite name.
 */
export const getPreferredTrackingFilterFavorites = () => {
  let favorites: TrackingFavoritePreferences = {};
  if (UserPreferences.isInitialized()) {
    const preferredFavorites = UserPreferences.get(TRACKING_FILTER_FAVORITES_PREFERENCE);
    if (isObject(preferredFavorites) && !Array.isArray(preferredFavorites)) {
      favorites = preferredFavorites as TrackingFavoritePreferences;
    }
  }
  return favorites;
};

/**
 * Reads persisted tracking filter category order from user preferences.
 *
 * @returns Saved tracking filter category ids in display order.
 */
export const getPreferredTrackingFilterCategoryOrder = () => {
  let categoryOrder: string[] = [];
  if (UserPreferences.isInitialized()) {
    const preferredOrder = UserPreferences.get(TRACKING_FILTER_CATEGORY_ORDER_PREFERENCE);
    if (Array.isArray(preferredOrder)) {
      categoryOrder = preferredOrder.filter(isString);
    }
  }
  return categoryOrder;
};

/**
 * Stores expanded tracking filter category ids for the application lifetime.
 */
export const trackingFilterExpandedIds = signal<string[]>([]);

/**
 * Replaces expanded tracking filter category ids.
 *
 * @param expandedIds Defines the expanded filter category ids.
 */
export const setTrackingFilterExpandedIds = createMutator(trackingFilterExpandedIds);

/**
 * Gets expanded tracking filter category ids from the in-memory signal.
 *
 * @returns Expanded tracking filter category ids.
 */
export const getTrackingFilterExpandedIds = () => trackingFilterExpandedIds.value;

/**
 * Subscribes to expanded tracking filter category id changes.
 *
 * @param onStoreChange Defines the callback invoked when expanded ids change.
 * @returns A function that unsubscribes from expanded id changes.
 */
export const subscribeTrackingFilterExpandedIds = (onStoreChange: () => void) => (
  trackingFilterExpandedIds.subscribe(() => {
    onStoreChange();
  })
);

/**
 * Asks for the tracking filter drawer to be opened with one category
 * expanded - the categories already expanded are left open beside it, and
 * one already among them is not listed twice.
 *
 * ```typescript
 *
 *   import {openTrackingFilterAtCategory} from '@/components/filter/current/store';
 *   ...
 *
 *   openTrackingFilterAtCategory(TrackingFilterDrawerCategoryId.GroupsAssets);
 *
 * ```
 *
 * @param categoryId Defines the category the drawer opens expanded at.
 */
export const openTrackingFilterAtCategory = (categoryId: TrackingFilterDrawerCategoryId) => {
  const expandedIds = getTrackingFilterExpandedIds();
  if (!expandedIds.includes(categoryId)) {
    setTrackingFilterExpandedIds([...expandedIds, categoryId]);
  }
  setTrackingFilterOpenRequested(true);
};

/**
 * Stores tracking filter favorites from user preferences.
 */
export const trackingFilterFavorites = signal<TrackingFavoritePreferences>({});

/**
 * Replaces and persists tracking filter favorites.
 *
 * @param favorites Defines the favorite filter states keyed by favorite name.
 */
export const setTrackingFilterFavorites = (favorites: TrackingFavoritePreferences) => {
  trackingFilterFavorites.value = favorites;
  if (UserPreferences.isInitialized()) {
    UserPreferences.set(TRACKING_FILTER_FAVORITES_PREFERENCE, favorites, true);
  }
};

/**
 * Initializes tracking filter favorites from user preferences when preferences are ready.
 */
export const initializeTrackingFilterFavorites = () => {
  if (UserPreferences.isInitialized()) {
    trackingFilterFavorites.value = getPreferredTrackingFilterFavorites();
  }
};

/**
 * Gets tracking filter favorites from the in-memory signal.
 *
 * @returns Favorite filter states keyed by favorite name.
 */
export const getTrackingFilterFavorites = () => trackingFilterFavorites.value;

/**
 * Subscribes to tracking filter favorite changes.
 *
 * @param onStoreChange Defines the callback invoked when favorites change.
 * @returns A function that unsubscribes from favorite changes.
 */
export const subscribeTrackingFilterFavorites = (onStoreChange: () => void) => (
  trackingFilterFavorites.subscribe(() => {
    onStoreChange();
  })
);

/**
 * Saves or replaces a single tracking filter favorite.
 *
 * @param name  Defines the favorite filter name.
 * @param state Defines the favorite filter state to save.
 */
export const saveTrackingFilterFavorite = (
  name: string,
  state: TrackingFavoriteFilterState
) => {
  setTrackingFilterFavorites({
    ...trackingFilterFavorites.value,
    [name] : state
  });
};

/**
 * Renames a tracking filter favorite while preserving its saved state.
 *
 * @param previousName Defines the existing favorite filter name.
 * @param nextName     Defines the replacement favorite filter name.
 */
export const renameTrackingFilterFavorite = (previousName: string, nextName: string) => {
  const favorites = {...trackingFilterFavorites.value};
  const state = favorites[previousName];
  delete favorites[previousName];
  if (state) {
    favorites[nextName] = state;
  }
  setTrackingFilterFavorites(favorites);
};

/**
 * Deletes a tracking filter favorite by name.
 *
 * @param name Defines the favorite filter name to delete.
 */
export const deleteTrackingFilterFavorite = (name: string) => {
  const favorites = {...trackingFilterFavorites.value};
  delete favorites[name];
  setTrackingFilterFavorites(favorites);
};

export interface TrackingWorkingFavorite {
  name: string;
  state: TrackingFavoriteFilterState;
}

/**
 * Stores the current session's selected or dirty favorite.
 */
export const trackingWorkingFavorite = signal<TrackingWorkingFavorite | null>(null);

/**
 * Replaces the current session's selected or dirty favorite.
 *
 * @param workingFavorite Defines the working favorite state, or null when none is selected.
 */
export const setTrackingWorkingFavorite = createMutator(trackingWorkingFavorite);

/**
 * Gets the current session's selected or dirty favorite.
 *
 * @returns The current working favorite, or null when none is selected.
 */
export const getTrackingWorkingFavorite = () => trackingWorkingFavorite.value;

/**
 * Subscribes to working favorite changes.
 *
 * @param onStoreChange Defines the callback invoked when the working favorite changes.
 * @returns A function that unsubscribes from working favorite changes.
 */
export const subscribeTrackingWorkingFavorite = (onStoreChange: () => void) => (
  trackingWorkingFavorite.subscribe(() => {
    onStoreChange();
  })
);

/**
 * Stores the working favorite as it stood when the filters were last
 * applied, so discarding the drawer's changes puts the favorite selection
 * back with the filters it belongs to.
 */
export const trackingAppliedFavorite = signal<TrackingWorkingFavorite | null>(null);

/**
 * Replaces the working favorite the applied filters were applied under.
 *
 * @param appliedFavorite Defines the applied favorite, or null when none was selected.
 */
export const setTrackingAppliedFavorite = createMutator(trackingAppliedFavorite);

/**
 * Gets the working favorite the applied filters were applied under.
 *
 * @returns The applied favorite, or null when none was selected.
 */
export const getTrackingAppliedFavorite = () => trackingAppliedFavorite.value;

/**
 * Stores tracking filter category order across browser sessions.
 */
export const trackingFilterCategoryOrder = signal<string[]>(
  []
);

/**
 * Replaces and persists tracking filter category order.
 *
 * @param categoryOrder Defines tracking filter category ids in display order.
 */
export const setTrackingFilterCategoryOrder = (categoryOrder: string[]) => {
  trackingFilterCategoryOrder.value = categoryOrder;
  if (UserPreferences.isInitialized()) {
    UserPreferences.set(TRACKING_FILTER_CATEGORY_ORDER_PREFERENCE, categoryOrder, true);
  }
};

/**
 * Initializes tracking filter category order from user preferences when preferences are ready.
 */
export const initializeTrackingFilterCategoryOrder = () => {
  if (UserPreferences.isInitialized()) {
    trackingFilterCategoryOrder.value = getPreferredTrackingFilterCategoryOrder();
  }
};

/**
 * Gets tracking filter category order from the in-memory signal.
 *
 * @returns Tracking filter category ids in display order.
 */
export const getTrackingFilterCategoryOrder = () => trackingFilterCategoryOrder.value;

/**
 * Subscribes to tracking filter category order changes.
 *
 * @param onStoreChange Defines the callback invoked when category order changes.
 * @returns A function that unsubscribes from category order changes.
 */
export const subscribeTrackingFilterCategoryOrder = (onStoreChange: () => void) => (
  trackingFilterCategoryOrder.subscribe(() => {
    onStoreChange();
  })
);
